/**
 * Axios instance + interceptors.
 * - Attaches `Authorization: Bearer <jwt>` from localStorage (CONTRACT §4 auth).
 * - Normalizes the error envelope `{ error: { code, message, details } }` (CONTRACT §4)
 *   into a typed `ApiError` so UI code can show clean toasts.
 * - On 401, clears the token and signals the auth layer to log out.
 * - On 5xx and network errors, best-effort Sentry capture (additive; never raises).
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import type { ApiErrorEnvelope } from "./types";
// Leaf module (no React imports) — importing the standalone helper here avoids a module-init cycle
// (client.ts → OrgContext.tsx → adminOrgs.ts → client.ts). OrgContext re-exports the same function.
import { getActiveOrgId } from "../locations/activeOrg";
// Leaf util (no app imports) — feeds the bug-report "recent requests" buffer.
import { recordRequest } from "../utils/bugCapture";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";
// Legacy localStorage keys — Phase 17 moved the refresh token to an httpOnly cookie and the access
// token in-memory. We only READ these on boot to migrate an existing session, then purge them.
const TOKEN_KEY = "eddkit.token";
const REFRESH_KEY = "eddkit.refresh";

// The access token now lives in MEMORY (per tab), not localStorage — so an XSS can't scrape it from
// storage, and it's gone on reload (rehydrated via a cookie refresh on boot).
let accessToken: string | null = null;

export function getToken(): string | null {
  return accessToken;
}

export function setToken(token: string | null): void {
  accessToken = token;
}

/** Legacy localStorage refresh token — present only for users who logged in before Phase 17. Read
 *  once on boot to migrate them onto the cookie. */
export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

/** Purge the legacy localStorage tokens (after migration / on logout). The current refresh token is
 *  an httpOnly cookie the server sets/clears; there's nothing else to store here. */
export function setRefreshToken(_token: string | null): void {
  try {
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage unavailable — nothing to purge */
  }
}

/** Mint a fresh access token via /auth/refresh. The refresh token rides in the httpOnly cookie
 *  (sent because withCredentials); a legacy localStorage token is sent in the body ONCE to migrate
 *  to the cookie, then purged. Bare axios (no interceptors) so a 401 here can't recurse. Returns the
 *  new access token, or null if there's no session. */
async function requestTokenRefresh(): Promise<string | null> {
  let legacy: string | null;
  try {
    legacy = localStorage.getItem(REFRESH_KEY);
  } catch {
    legacy = null;
  }
  try {
    const { data } = await axios.post<{ access_token: string }>(
      `${API_BASE}/auth/refresh`,
      legacy ? { refresh_token: legacy } : {},
      { withCredentials: true, headers: { "Content-Type": "application/json", "X-CSRF": "1" } },
    );
    setToken(data.access_token);
    if (legacy) setRefreshToken(null); // migrated to the cookie — drop the localStorage copy
    return data.access_token;
  } catch {
    return null;
  }
}

// Single-flight: many requests can 401 at once when the access token expires; they all await ONE
// refresh instead of stampeding /auth/refresh (which would rotate the token out from under each other).
let refreshInFlight: Promise<string | null> | null = null;
function refreshOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestTokenRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Boot rehydration: attempt one cookie/legacy refresh to recover the session on app load. Shares
 *  the single-flight with the interceptor so first-paint queries don't stampede /auth/refresh. */
export function bootstrapSession(): Promise<string | null> {
  return refreshOnce();
}

export function clearSession(): void {
  setToken(null);
  setRefreshToken(null);
}

/** A normalized error the whole app can rely on. */
export class ApiError extends Error {
  code: string;
  status?: number;
  details?: unknown;

  constructor(message: string, code = "error", status?: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/** Hook the auth layer registers so a 401 can force a logout without a circular import. */
let onUnauthorized: (() => void) | null = null;
export function registerUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json", "X-CSRF": "1" },
  withCredentials: true, // send/receive the httpOnly refresh cookie on same-origin auth calls
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  // X-Active-Org: sent only when a super-admin has switched labs (localStorage["eddkit.active_org"]).
  // Non-super-admins never have this set, so the header is never sent for them. The server validates
  // it against UserOrg on EVERY request — a forged/edited value grants nothing (it 403s a non-member).
  const activeOrgId = getActiveOrgId();
  if (activeOrgId) {
    config.headers.set("X-Active-Org", activeOrgId);
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    try {
      recordRequest(response.config?.method ?? "", response.config?.url ?? "", response.status);
    } catch {
      /* capture is best-effort */
    }
    return response;
  },
  async (error: AxiosError<ApiErrorEnvelope>) => {
    try {
      recordRequest(
        error.config?.method ?? "",
        error.config?.url ?? "",
        error.response?.status ?? 0,
      );
    } catch {
      /* capture is best-effort */
    }
    const status = error.response?.status;
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (status === 401) {
      const url = original?.url ?? "";
      const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh");
      // The access token likely just expired — try ONE silent refresh (the refresh token is an
      // httpOnly cookie we can't read, so we always attempt rather than precondition on it) and
      // replay the request before giving up. Skip the login/refresh calls themselves (a 401 there is
      // terminal) and never retry the same request twice.
      if (original && !original._retry && !isAuthCall) {
        original._retry = true;
        const newToken = await refreshOnce();
        if (newToken) {
          // A 401 is raised before the handler runs, so replaying is safe — EXCEPT for a body that
          // can't be re-read (a raw Blob/ArrayBuffer/stream upload). FormData and JSON replay fine;
          // for a consumed stream, we've still refreshed the token (so the next attempt works) but
          // don't risk re-sending a corrupted body — let this one surface its error.
          const body: unknown = original.data;
          const nonReplayable =
            (typeof Blob !== "undefined" && body instanceof Blob) ||
            (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer) ||
            (typeof ReadableStream !== "undefined" && body instanceof ReadableStream);
          if (!nonReplayable) {
            original.headers.set("Authorization", `Bearer ${newToken}`);
            return api(original); // replay the original request with the fresh token
          }
        }
      }
      // Refresh failed (or this was an auth call) → the session is truly over.
      clearSession();
      onUnauthorized?.();
    }

    // Best-effort Sentry capture for 5xx and network errors.
    // Skip expected auth noise (401 = session expiry, 403 = permissions — not bugs).
    const shouldCapture =
      error.code === "ERR_NETWORK" ||
      (status !== undefined && status >= 500);
    if (shouldCapture) {
      // Defer the analytics import (a dynamic import breaks the sync import cycle); captureError is
      // a no-op when VITE_SENTRY_DSN is unset, so this is additive and never raises.
      void import("../analytics")
        .then(({ captureError }) => {
          captureError(error, {
            url: error.config?.url,
            method: error.config?.method,
            status,
          });
        })
        .catch(() => {
          // never raise
        });
    }

    // Unwrap the CONTRACT §4 envelope when present.
    const envelope = error.response?.data;
    if (envelope && typeof envelope === "object" && "error" in envelope && envelope.error) {
      const e = envelope.error;
      return Promise.reject(
        new ApiError(
          e.message || "Request failed",
          e.code || "error",
          status,
          e.details,
        ),
      );
    }

    // Network / non-enveloped errors.
    if (error.code === "ERR_NETWORK") {
      return Promise.reject(
        new ApiError(
          "Cannot reach the Prisma EDD Builder API. Is the backend running?",
          "network_error",
          status,
        ),
      );
    }

    return Promise.reject(
      new ApiError(error.message || "Request failed", error.code || "error", status),
    );
  },
);

/** Helper to coerce any thrown value to a human-readable message for toasts. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}
